import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Mail, LifeBuoy, Loader2, Send } from "lucide-react";

export const SUPPORT_EMAIL = "huzaifahwaheed@sitesux.com";

const FAQS = [
  {
    q: "How do I book a project or request a call back?",
    a: "Click “Request a Call Back” anywhere on the site and fill out the quick 6-step brief. Pick your preferred date and time window, and we’ll call you — you’ll also get a free client dashboard to track everything.",
  },
  {
    q: "How does pricing and payment work?",
    a: "Every build is scoped to your needs. We discuss pricing and timeline together on your call — no surprises. A deposit secures your spot on the schedule, and the balance is handled as the project progresses.",
  },
  {
    q: "Do I really own the source code?",
    a: "Yes — 100%. We don’t lock you into bloated website builders. You receive the pure custom source code and full ownership of your digital asset, forever.",
  },
  {
    q: "How do I track my project’s progress?",
    a: "Log into your client dashboard. You’ll see a live progress tracker across every phase and built-in chat with our team — start to finish.",
  },
  {
    q: "Will I get notified about updates?",
    a: "Yes. We email you when your project status changes and when you get a new message. Just make sure your email is verified.",
  },
  {
    q: "I still need help — how do I reach a human?",
    a: "Use the contact form below or email us anytime. We usually respond within one business day.",
  },
];

export default function Support() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/support/contact", { name, email, message });
      toast.success("Message sent! We'll get back to you soon.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-background text-white" data-testid="support-page">
      <Header onSchedule={() => (window.location.href = "/")} />

      <section className="mx-auto max-w-5xl px-6 pb-24 pt-16 sm:pt-20">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-primary">
            <LifeBuoy className="h-3.5 w-3.5" /> Support
          </span>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
            We're here to help.
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-300">
            Question about your project, a booking, or your dashboard? Reach us directly or send a message below.
          </p>
        </div>

        {/* CONTACT CARD */}
        <div className="mt-10">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="support-email-link"
            className="group flex items-center gap-4 border border-white/10 bg-[#0A0A0B] p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-primary/50 sm:max-w-md"
          >
            <div className="flex h-11 w-11 items-center justify-center border border-white/10 bg-white/[0.03] text-primary group-hover:border-primary/50">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Email us</p>
              <p className="mt-1 font-display text-lg font-semibold">{SUPPORT_EMAIL}</p>
            </div>
          </a>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* CONTACT FORM */}
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Send us a message</h2>
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="support-form">
              <div>
                <Label className="text-zinc-400">Your name</Label>
                <Input data-testid="support-name" value={name} onChange={(e) => setName(e.target.value)}
                  required className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-zinc-400">Your email</Label>
                <Input data-testid="support-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="you@company.com" />
              </div>
              <div>
                <Label className="text-zinc-400">How can we help?</Label>
                <Textarea data-testid="support-message" value={message} onChange={(e) => setMessage(e.target.value)}
                  required rows={5} className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Tell us what's going on…" />
              </div>
              <Button data-testid="support-submit-btn" type="submit" disabled={loading || !name || !email || !message}
                className="w-full rounded-full bg-primary text-white hover:bg-primary/90">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send message
              </Button>
            </form>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Frequently asked</h2>
            <Accordion type="single" collapsible className="mt-5" data-testid="support-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left text-sm font-semibold hover:text-primary hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-zinc-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 sm:flex-row">
          <p className="font-display font-black tracking-tighter text-lg">
            SITES<span className="text-primary"> UX</span>
          </p>
          <p className="font-mono text-xs text-zinc-500">© 2026 Sites UX — you own the code.</p>
        </div>
      </footer>
    </div>
  );
}
